import { Injectable } from '@angular/core';
import { Http, Headers, RequestOptions } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';

import {
  SkillAiDateRangeParams,
  SkillAiStatisticsSummaryResponse,
  SkillAiTimeseriesItem,
  SkillAiTopUserItem,
  SkillAiTopSpecializationItem,
  SkillAiLogsResponse,
  SkillAiLogsQueryParams,
  SkillAiBudgetStatusApiResponse,
  StandardAiQuestionStatisticsResponse,
  StandardAiQuestionStatisticsRequest,
  SkillLearningPopularityPageResponse,
  SkillLearningPopularityRequest,
  ContentTranslationCoverageResponse,
} from '../models/skill-learning.model';
import { SKILL_AI_STATISTICS_API } from '../constants/skill-api.constants';

interface QueryParamsMap {
  [key: string]: string | number | boolean | null | undefined;
}

@Injectable()
export class SkillAiStatisticsApiService {

  private readonly jsonHeaders = new Headers({ 'Content-Type': 'application/json' });
  private readonly jsonOptions = new RequestOptions({ headers: this.jsonHeaders });

  constructor(private http: Http) {}

  fetchSummary(params: SkillAiDateRangeParams): Observable<SkillAiStatisticsSummaryResponse> {
    return this.http
      .get(this.buildUrl(SKILL_AI_STATISTICS_API.SUMMARY, this.buildRangeQueryParams(params)))
      .map(response => response.json() as SkillAiStatisticsSummaryResponse);
  }

  fetchTimeseries(params: SkillAiDateRangeParams): Observable<SkillAiTimeseriesItem[]> {
    return this.http
      .get(this.buildUrl(SKILL_AI_STATISTICS_API.TIMESERIES, this.buildRangeQueryParams(params)))
      .map(response => response.json() as SkillAiTimeseriesItem[]);
  }

  fetchTopUsers(params: SkillAiDateRangeParams, limit: number): Observable<SkillAiTopUserItem[]> {
    return this.http
      .get(this.buildUrl(SKILL_AI_STATISTICS_API.TOP_USERS, this.extendWithLimit(params, limit)))
      .map(response => response.json() as SkillAiTopUserItem[]);
  }

  fetchTopSpecializations(params: SkillAiDateRangeParams, limit: number): Observable<SkillAiTopSpecializationItem[]> {
    return this.http
      .get(this.buildUrl(SKILL_AI_STATISTICS_API.TOP_SPECIALIZATIONS, this.extendWithLimit(params, limit)))
      .map(response => response.json() as SkillAiTopSpecializationItem[]);
  }

  fetchLogs(params: SkillAiLogsQueryParams): Observable<SkillAiLogsResponse> {
    return this.http
      .post(
        SKILL_AI_STATISTICS_API.LOGS,
        JSON.stringify(this.buildLogsRequest(params)),
        this.jsonOptions,
      )
      .map(response => response.json() as SkillAiLogsResponse);
  }

  fetchBudgetStatus(): Observable<SkillAiBudgetStatusApiResponse> {
    return this.http
      .get(SKILL_AI_STATISTICS_API.BUDGET_STATUS)
      .map(response => response.json() as SkillAiBudgetStatusApiResponse);
  }

  fetchStandardQuestionStatistics(
    params: StandardAiQuestionStatisticsRequest,
  ): Observable<StandardAiQuestionStatisticsResponse> {
    return this.http
      .post(
        SKILL_AI_STATISTICS_API.STANDARD_QUESTIONS_STATISTICS,
        JSON.stringify(this.buildStandardQuestionRequest(params)),
        this.jsonOptions,
      )
      .map(response => response.json() as StandardAiQuestionStatisticsResponse);
  }

  fetchLearningPopularityStatistics(
    params: SkillLearningPopularityRequest,
  ): Observable<SkillLearningPopularityPageResponse> {
    return this.http
      .post(
        SKILL_AI_STATISTICS_API.LEARNING_POPULARITY_STATISTICS,
        JSON.stringify(this.buildLearningPopularityRequest(params)),
        this.jsonOptions,
      )
      .map(response => response.json() as SkillLearningPopularityPageResponse);
  }

  fetchTranslationCoverageStatistics(): Observable<ContentTranslationCoverageResponse> {
    return this.http
      .get(SKILL_AI_STATISTICS_API.TRANSLATION_COVERAGE_STATISTICS)
      .map(response => response.json() as ContentTranslationCoverageResponse);
  }

  private extendWithLimit(params: SkillAiDateRangeParams, limit: number): QueryParamsMap {
    return {
      from: params.from,
      to: params.to,
      limit: limit,
    };
  }

  private buildRangeQueryParams(params: SkillAiDateRangeParams): QueryParamsMap {
    return {
      from: params.from,
      to: params.to,
    };
  }

  private buildLogsRequest(params: SkillAiLogsQueryParams): QueryParamsMap {
    return {
      from: params.from,
      to: params.to,
      page: params.page,
      size: params.size,
      operation: params.operation,
      success: params.success,
      model: params.model,
      userId: params.userId,
      specializationId: params.specializationId,
    };
  }

  private buildStandardQuestionRequest(
    params: StandardAiQuestionStatisticsRequest,
  ): StandardAiQuestionStatisticsRequest {
    return {
      page: params.page,
      size: params.size,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    };
  }

  private buildLearningPopularityRequest(
    params: SkillLearningPopularityRequest,
  ): SkillLearningPopularityRequest {
    return {
      page: params.page,
      size: params.size,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    };
  }

  private buildUrl(base: string, params: QueryParamsMap): string {
    const query = this.buildQuery(params);
    if (!query) {
      return base;
    }
    return `${base}?${query}`;
  }

  private buildQuery(params: QueryParamsMap): string {
    return Object.keys(params)
      .filter(key => this.hasValue(params[key]))
      .map(key => this.buildQueryPart(key, params[key]))
      .join('&');
  }

  private buildQueryPart(key: string, value: string | number | boolean | null | undefined): string {
    return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
  }

  private hasValue(value: string | number | boolean | null | undefined): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/services/skill-ai-statistics-api.service.ts